import GetServerTime from 'app/containers/rtc/BookingContext/GetServerTime';
import { BookingId, BookingState, BookingType } from 'app/models/rtc/booking/constants';
import { getBookingState } from 'app/models/rtc/booking/selectors';
import { ParticipantType } from 'app/models/rtc/constants';
import Logger from 'app/services/logger';
import { AppState } from 'app/store';
import isEqual from 'lodash/isEqual';
import React, { Component, createContext, useContext } from 'react';
import { connect } from 'react-redux';
import { createSelectorCreator, defaultMemoize } from 'reselect';

interface IProps {
  bookingId?: BookingId;
  bookingType?: BookingType;
  hostKey?: string;
  participantType?: ParticipantType;
}

// Exported primarily for testing purposes.
export const BookingStateContext = createContext<BookingState | undefined>(undefined);

interface IBookingStateRendererProps {
  value: BookingState | undefined;
}

// Sole existence is due to the fact that `connect` type definitons don't accept context.
class BookingStateRenderer extends Component<IBookingStateRendererProps> {
  public componentDidUpdate(prevProps: IBookingStateRendererProps) {
    if (prevProps.value !== this.props.value) {
      Logger.info('Booking state has changed', { bookingState: this.props.value });
    }
  }

  public render() {
    return (<BookingStateContext.Provider {...this.props} />);
  }
}

const BookingStateConnect = connect(
  () => {
    const computeBookingState: typeof getBookingState =
      createSelectorCreator(defaultMemoize, isEqual)(
        [getBookingState],
        (bookingState: BookingState | undefined) => bookingState,
      );

    return (
      state: AppState,
      { bookingId, bookingType, currentTime, hostKey, participantType }: IProps & { currentTime: Date },
    ) => {
      if (bookingId && bookingType && participantType !== undefined) {
        return { value: computeBookingState(state, bookingType, bookingId, participantType, currentTime, hostKey) };
      } else {
        return { value: undefined };
      }
    };
  },
)(BookingStateRenderer);

/**
 * Will generate the booking state based on properties passed in.
 */
export class BookingStateProvider extends Component<IProps> {
  public render() {
    return (
      <GetServerTime>{(currentTime) => (
        <BookingStateConnect {...this.props} currentTime={currentTime} />
      )}</GetServerTime>
    );
  }
}

export const BookingStateConsumer = BookingStateContext.Consumer;
export const useBookingState = () => useContext(BookingStateContext);
